import { tagPresets, defaultTagConfig } from "../config/tagPresets";

/**
 * Retorna a configuração de etiqueta ativa para o evento especificado.
 * Prioridade:
 * 1. Configuração salva no LocalStorage (Ajustes específicos locais)
 * 2. Preset pré-definido por ID de Evento em `tagPresets.js`
 * 3. Configuração padrão global (Zebra 9x5cm)
 * 
 * @param {string|number} eventId ID do evento
 * @returns {object} Configuração de etiqueta ativa
 */
export const getTagConfig = (eventId) => {
    const id = String(eventId);
    
    // 1. Tentar ler do LocalStorage
    try {
        const localSaved = localStorage.getItem(`tag_config_${id}`);
        if (localSaved) {
            const parsed = JSON.parse(localSaved);
            if (validateTagConfig(parsed)) {
                return parsed;
            }
        }
    } catch (error) {
        console.error("Erro ao ler configuração de tag do LocalStorage:", error);
    }
    
    // 2. Tentar buscar preset pré-cadastrado no código para este evento específico
    if (tagPresets.events && tagPresets.events[id]) {
        return { ...tagPresets.events[id] };
    }
    
    // 3. Fallback para a configuração padrão global
    return { ...defaultTagConfig };
};

/**
 * Salva a configuração de etiqueta no LocalStorage do navegador para o evento atual.
 * 
 * @param {string|number} eventId ID do evento
 * @param {object} config Configuração de etiqueta a ser salva
 * @returns {boolean} Retorna true se salvou com sucesso
 */
export const saveTagConfig = (eventId, config) => {
    const id = String(eventId);
    if (!validateTagConfig(config)) {
        console.error("Configuração de etiqueta inválida para salvar:", config);
        return false;
    }
    
    try {
        localStorage.setItem(`tag_config_${id}`, JSON.stringify(config));
        return true;
    } catch (error) {
        console.error("Erro ao salvar configuração de tag no LocalStorage:", error);
        return false;
    }
};

/**
 * Valida os campos básicos de uma configuração de etiqueta.
 * 
 * @param {object} config Objeto de configuração
 * @returns {boolean}
 */
export const validateTagConfig = (config) => {
    if (!config || typeof config !== "object") return false;
    
    const requiredFields = ["widthMm", "heightMm"];
    const hasRequired = requiredFields.every(field => typeof config[field] === "number" && config[field] > 0);
    
    return hasRequired;
};

/**
 * Exporta a configuração para uma string JSON formatada.
 * 
 * @param {object} config 
 * @returns {string}
 */
export const exportTagConfig = (config) => {
    try {
        return JSON.stringify(config, null, 2);
    } catch (error) {
        console.error("Erro ao exportar configuração de tag:", error);
        return "";
    }
};

/**
 * Importa uma configuração a partir de uma string JSON, validando sua estrutura.
 * 
 * @param {string} jsonString 
 * @returns {object|null} Retorna o objeto se for válido, senão null
 */
export const importTagConfig = (jsonString) => {
    try {
        const parsed = JSON.parse(jsonString);
        
        // Garantir que tipos numéricos estejam corretos após importação
        const normalized = {
            ...parsed,
            widthMm: Number(parsed.widthMm),
            heightMm: Number(parsed.heightMm),
            qrCodeSizeMm: Number(parsed.qrCodeSizeMm),
            nameFontSize: Number(parsed.nameFontSize),
            companyFontSize: Number(parsed.companyFontSize),
            roleFontSize: Number(parsed.roleFontSize),
            qrCodePosition: {
                top: Number(parsed.qrCodePosition?.top),
                left: Number(parsed.qrCodePosition?.left),
            },
            namePosition: {
                top: Number(parsed.namePosition?.top),
                left: Number(parsed.namePosition?.left),
            },
            companyPosition: {
                top: Number(parsed.companyPosition?.top),
                left: Number(parsed.companyPosition?.left),
            },
            rolePosition: {
                top: Number(parsed.rolePosition?.top),
                left: Number(parsed.rolePosition?.left),
            }
        };
        
        if (validateTagConfig(normalized)) {
            return normalized;
        }
    } catch (error) {
        console.error("Erro ao analisar JSON para importar configuração:", error);
    }
    return null;
};
